import React, { FC, useContext } from "react";
import {
  formatCityStateZipByCountry,
  formatFullName,
  formatStreetByCountry,
} from "@pepdirect/v3/helpers/address";
import { formatPhoneByCountry } from "@pepdirect/v3/helpers/phone";
import {
  BillingAddress,
  CreditCardBillingAddress,
  ShippingAddress,
} from "v3/services/graphql/generated";
import { StyledAddress, StyledSpecialInstructions } from "./index.styled";
import { TenantContext } from "v3/context/tenant";

interface AddressDetailsProps {
  address: ShippingAddress | BillingAddress | CreditCardBillingAddress | null;
  className?: string;
  phone?: string | null;
}

export const AddressDetails: FC<AddressDetailsProps> = ({
  address,
  className = "",
  phone,
}) => {
  const { tenant } = useContext(TenantContext);
  const showAddressNickname =
    !!tenant?.contactConfig.shippingAddressConfig.addressNickname;
  const showSpecialInstructions =
    !!tenant?.contactConfig.shippingAddressConfig.addressSpecialInstructions;
  if (!address) return null;
  const taxId = "taxId" in address ? address.taxId : null;
  const nickname = "nickname" in address ? address.nickname : null;
  const specialInstructions =
    "specialInstructions" in address ? address.specialInstructions : null;

  const {
    city,
    company,
    countryCode: countryFormat,
    firstName,
    lastName,
    line1,
    line2,
    state,
    zip,
  } = address;

  const fullName = formatFullName({ firstName, lastName, countryFormat });
  const streetAddress = formatStreetByCountry({ line1, line2, countryFormat });
  const cityStateZip = formatCityStateZipByCountry({
    city,
    state,
    zip,
    countryFormat,
  });

  // don't only display zip if that was entered instead of a billing address
  const isZipOnly = !!zip && !fullName && !streetAddress && !city && !state;
  if (isZipOnly) return null;

  return (
    <StyledAddress className={className}>
      {showAddressNickname && nickname && <div>{nickname}</div>}
      {fullName && <div>{fullName}</div>}
      {company && <div>{company}</div>}
      {taxId && <div>{taxId}</div>}
      <div>{streetAddress}</div>
      <div>{cityStateZip}</div>
      {phone && <div>{formatPhoneByCountry(phone, countryFormat)}</div>}
      {showSpecialInstructions && specialInstructions && (
        <StyledSpecialInstructions>
          {specialInstructions}
        </StyledSpecialInstructions>
      )}
    </StyledAddress>
  );
};
